import type { ReportDocument } from '@wyocspay/ui';
import type { ControlCommand } from './device.js';

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://127.0.0.1:4010';
const STORAGE_KEY = 'wyocspay.admin.session';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

export const getToken = () => sessionStorage.getItem(STORAGE_KEY);
export const setToken = (t: string | null) =>
  t ? sessionStorage.setItem(STORAGE_KEY, t) : sessionStorage.removeItem(STORAGE_KEY);

/**
 * Signal that the session is gone.
 *
 * Admin sessions are 15 minutes by design, so expiry during ordinary use is
 * the normal case, not an edge case. Clearing the token was not enough: the
 * view that made the request rendered "Authentication required" and sat there,
 * leaving someone staring at an error instead of a way back in. The app
 * subscribes and returns to the sign-in screen.
 */
type UnauthorisedHandler = () => void;
let onUnauthorised: UnauthorisedHandler | null = null;

export function setUnauthorisedHandler(fn: UnauthorisedHandler | null): void {
  onUnauthorised = fn;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  const text = await response.text();
  const body = text ? (JSON.parse(text) as unknown) : {};
  if (!response.ok) {
    const err = (body as { error?: { code?: string; message?: string } }).error;
    if (response.status === 401) {
      setToken(null);
      onUnauthorised?.();
    }
    throw new ApiError(response.status, err?.code ?? 'unknown', err?.message ?? 'Заявката е неуспешна.');
  }
  return body as T;
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined });

export interface Merchant {
  id: string;
  name: string;
  email: string;
  country: string;
  status: string;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  requirements_due: string[];
  stripe_account_id: string | null;
  payments: number;
  fee: { percent_bps: number; fixed: number };
  version: number;
  created_at: string;
}

export interface MerchantCurrencyAdvice {
  /** Currency WyoCSPay preselects when this merchant creates new work. */
  profile_currency: string;
  /** Country-derived fallback used before a live Stripe account exists. */
  country_currency: string;
  /** Freshly read from the live connected account, never the test account. */
  stripe_currency: string | null;
  stripe_country: string | null;
  recommended_currency: string;
  recommendation_source: 'stripe' | 'country';
  profile_matches_recommendation: boolean;
  stripe_check_status: 'verified' | 'not_connected' | 'unavailable';
  supported_currencies: string[];
}

export interface AdminOverview {
  merchants: Record<string, number>;
  /** Per currency. Summing these would produce a figure that means nothing. */
  volume_30d: Array<{ currency: string; amount: number }>;
  /** One reporting figure, with the converted portion called out. */
  platform_revenue_30d: {
    total: number;
    exact: number;
    estimated: number;
    currency: string;
    applied: Array<{ currency: string; amount: number; rate: number; converted: number }>;
    missingRates: string[];
  };
  payments_30d: number;
  open_alerts: Record<string, number>;
  pending_webhook_deliveries: number;
  last_reconciliation: {
    id: string;
    started_at: string;
    finished_at: string | null;
    status: string;
    checked: number;
    discrepancies: number;
    auto_corrected: number;
    escalated: number;
  } | null;
}

export interface Device {
  id: string;
  name: string;
  device_type: string;
  os: string | null;
  key_id: string;
  status: string;
  registered_at: string;
  last_seen_at: string | null;
  last_seen_ip: string | null;
  revoked_at: string | null;
  revoked_reason: string | null;
  user: { id: string; name: string; email: string; role: string };
}

/** Един телефон, регистриран да приема плащания на място. */
export interface AdminTerminal {
  id: string;
  label: string;
  status: string;
  merchant: { id: string; name: string; country: string; status: string };
  /** Самоописание от устройството. Показва се като такова, не се вярва на него. */
  platform: string | null;
  os_version: string | null;
  app_version: string | null;
  device_model: string | null;
  /**
   * Идентификаторът на инсталацията. Уникален за платформата, затова е и
   * единственото, което свързва един и същ апарат през отнемане и повторно
   * сдвояване - включително при друг търговец.
   */
  install_id: string;
  registered_at: string;
  registered_by: string;
  last_seen_at: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  revoked_reason: string | null;
  /** Последното, което се е счупило на този апарат, или null ако нищо. */
  last_problem: TerminalProblem | null;
}

/**
 * Провал на терминал, така както го е видял човекът на касата.
 *
 * Текстът идва от онази страна, която знае какво се е случило: приложението за
 * всичко вътре в Terminal SDK, сървърът за собствените си откази. Никой не
 * преформулира другия, затова тук пише точно каквото е прочел операторът.
 */
export interface TerminalProblem {
  at: string;
  actor: string;
  code: string;
  stage: 'setup' | 'reader' | 'payment' | 'capture' | 'network';
  message: string;
  /** True, когато самият телефон трябва да се промени, за да приема карти. */
  permanent: boolean;
}

/**
 * Къде Tap to Pay изобщо може да работи, според списъците на Stripe.
 *
 * Двете платформи се отчитат поотделно, защото наистина се различават: бизнес
 * само с iPhone в държава, поддържана единствено за Android, е блокиран, а един
 * общ флаг „поддържа се“ би му казал точно обратното.
 */
export interface TapToPayAvailability {
  ios: boolean;
  android: boolean;
  unavailable: boolean;
  country: string;
  listsCheckedOn: string;
}

export interface TerminalMerchant {
  id: string;
  name: string;
  country: string;
  status: string;
  granted: boolean;
  granted_at: string | null;
  granted_by: string | null;
  tap_to_pay: TapToPayAvailability;
  terminals: { active: number; pending: number; rejected: number; revoked: number };
}

export interface TerminalReview {
  terminal: {
    id: string;
    label: string;
    status: string;
    platform: string | null;
    os_version: string | null;
    app_version: string | null;
    device_model: string | null;
    install_id: string;
    registered_at: string;
    registered_by: string;
  };
  merchant: {
    id: string;
    name: string;
    country: string;
    status: string;
    charges_enabled: boolean;
    details_submitted: boolean;
  };
  /** Адресът, който Stripe е проверил и от който се създава локацията. */
  verified_address: {
    line1: string | null;
    line2: string | null;
    city: string | null;
    state: string | null;
    postal_code: string | null;
    country: string | null;
  } | null;
  address_error: string | null;
  mode: 'live' | 'test';
}

export interface TerminalHistory {
  terminal: { id: string; label: string; merchant: { id: string; name: string } };
  data: Array<{ sequence: number; at: string; action: string; actor: string; data: unknown }>;
}

export interface AuditPage {
  chain_valid: boolean;
  chain_entries: number;
  chain_broken_at: number | null;
  chain_break_reason: string | null;
  data: Array<{
    sequence: number;
    at: string;
    action: string;
    actor: string;
    subject: string;
    data: unknown;
    hash: string;
    ip: string | null;
  }>;
}

export interface MerchantApplication {
  id: string;
  business_name: string;
  contact_name: string;
  email: string;
  country: string;
  phone: string | null;
  website: string | null;
  message: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  status: string; // PENDING | INFO_REQUESTED | APPROVED | REJECTED
  info_request: string | null;
  review_note: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  merchant_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionApplicationRow {
  id: string;
  merchantId: string;
  tier: 'special' | 'vipplus';
  status: 'PENDING' | 'INFO_REQUESTED' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  justification: string | null;
  volume: string | null;
  currency: string;
  floorAmount: number;
  suggestedAmount: number;
  adminAmount: number | null;
  infoRequest: string | null;
  reviewNote: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  subscriptionId: string | null;
  createdAt: string;
  merchant: { id: string; name: string; email: string; country: string; feePayer: string; subscriptionTier: string };
}

export interface SubscriptionAnalysis {
  tier: 'special' | 'vipplus';
  currency: string;
  region: string;
  months: number;
  from: string;
  to: string;
  monthlyPlatformFee: number;
  monthlyStripeFeeEst: number;
  analysisFloor: number;
  specialPrice: number;
  floor: number;
  marginBps: number;
  suggested: number;
  vipplusFullyEffective: boolean;
  fxNote: string | null;
}

export interface MerchantSubscriptionRow {
  id: string;
  merchantId: string;
  tier: 'special' | 'vipplus';
  status: 'PENDING_PAYMENT' | 'ACTIVE' | 'PAST_DUE' | 'CANCELLED';
  monthlyAmount: number;
  currency: string;
  currentPeriodEnd: string | null;
  createdAt: string;
  merchant: { id: string; name: string; email: string; country: string; feePayer: string };
}

export interface DocumentSignatory {
  name: string;
  title: string;
  /** Signature image as a data: URL (drawn or uploaded + cropped). */
  signature: string;
}

export interface AdminFormFieldOption {
  key: string;
  label: string;
}

/** A field on a fillable document (mirrors the API registry FormField). */
export interface AdminFormField {
  token: string;
  label: string;
  type: 'text' | 'textarea' | 'date' | 'number' | 'select' | 'radio';
  options?: AdminFormFieldOption[];
  section?: string;
  placeholder?: string;
  help?: string;
  span?: 1 | 2;
}

export interface AdminDocumentRow {
  id: string;
  merchant_id: string;
  merchant_name: string;
  merchant_email: string;
  document_key: string;
  title: string;
  number: string;
  status: string;
  reason: string;
  issued_at: string;
  accepted_at: string | null;
  accepted_name: string | null;
  declined_at: string | null;
}

export interface OnboardingRow {
  id: string;
  name: string;
  email: string;
  country: string;
  status: string;
  submitted_at: string | null;
  reviewed_at: string | null;
  document_count: number;
}

export interface MerchantOnboarding {
  status: string;
  rejection_reason: string | null;
  submitted_at: string | null;
  data: {
    company_registration_no: string | null;
    tax_id: string | null;
    vat: string | null;
    dba: string | null;
    representative_title: string | null;
    representative_role: string | null;
  };
  documents: Array<{
    id: string;
    kind: string;
    label: string | null;
    filename: string;
    mimeType: string;
    size: number;
    uploadedAt: string;
  }>;
}

export const api = {
  /** Redeem a one-time enrolment code. Unauthenticated by necessity. */
  enrolDevice: (body: { code: string; public_key_pem: string; device_name: string; os?: string }) =>
    post<{ device_id: string; user_id: string; user_name: string }>('/auth/device-enrolment', body),

  /**
   * Sign in.
   *
   * No device is asked for. What proves the machine is the TLS client
   * certificate installed in the operating system keychain, which the browser
   * presents on the connection itself - it is never carried in the request body
   * and this code cannot see it.
   *
   * `deviceId` is passed only when this browser still holds a key from before
   * certificates existed, so that a browser enrolled the old way keeps working
   * through the changeover. Nothing in the console creates one any more.
   */
  login: (email: string, password: string, deviceId?: string | null) =>
    post<{
      token: string;
      /** PCI DSS 8.3.5 / 8.3.9 - the session may do nothing else until this is done. */
      password_change_required: boolean;
      user: { id: string; name: string; email: string; role: string };
    }>('/auth/login', {
      email,
      password,
      scope: 'admin',
      ...(deviceId ? { device_id: deviceId } : {}),
    }),
  logout: () => post<{ ok: boolean }>('/auth/logout'),
  me: () =>
    request<{
      user: { id: string; name: string; email: string; role: string };
      session: {
        scope: string;
        expires_at: string;
        device_id: string | null;
        certificate_id: string | null;
        password_change_required: boolean;
      };
    }>('/auth/me'),

  /**
   * Every session ends when this succeeds, this one included, so the caller
   * must send the user back to sign-in rather than carrying on.
   */
  changePassword: (current_password: string, new_password: string) =>
    post<{ ok: boolean; sessions_terminated: number; sign_in_again: boolean }>('/auth/password', {
      current_password,
      new_password,
    }),

  overview: () => request<AdminOverview>('/admin/overview'),

  /** Pending counts per sidebar section (only non-zero keys), for the nav. */
  navCounts: () => request<{ counts: Record<string, number> }>('/admin/nav-counts'),

  // --- Legal documents (operator) ---
  documentSignatory: () => request<DocumentSignatory>('/admin/document-signatory'),
  saveDocumentSignatory: (body: DocumentSignatory) =>
    post<{ ok: true }>('/admin/document-signatory', body),
  adminDocuments: (params: { merchantId?: string; status?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.merchantId) q.set('merchantId', params.merchantId);
    if (params.status) q.set('status', params.status);
    const qs = q.toString();
    return request<{ documents: AdminDocumentRow[] }>(`/admin/documents${qs ? `?${qs}` : ''}`);
  },
  adminDocumentHtml: async (id: string): Promise<string> => {
    const token = getToken();
    const res = await fetch(`${BASE}/admin/documents/${encodeURIComponent(id)}/render`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new ApiError(res.status, 'document_failed', 'Документът не можа да се зареди.');
    return res.text();
  },
  /** A fillable document's fields (both parties), current data and state. */
  documentForm: (merchantId: string, key: string) =>
    request<{
      merchantFields: AdminFormField[];
      reviewerFields: AdminFormField[];
      merchant: Record<string, string>;
      reviewer: Record<string, string>;
      state: { state?: string; number?: string | null };
    }>(`/admin/documents/${encodeURIComponent(merchantId)}/${encodeURIComponent(key)}/form`),
  /** Save the operator's answers to a document's reviewer fields. */
  saveReviewerForm: (merchantId: string, key: string, values: Record<string, string>) =>
    post<{ ok: true; values: Record<string, string> }>(
      `/admin/documents/${encodeURIComponent(merchantId)}/${encodeURIComponent(key)}/reviewer-form`,
      values,
    ),
  /** Finalize an internal reviewer-completed document: seal + operator signature. */
  issueReviewedDocument: (merchantId: string, key: string) =>
    post<{ ok: true; number: string }>(
      `/admin/documents/${encodeURIComponent(merchantId)}/${encodeURIComponent(key)}/issue`,
      {},
    ),
  /** Render a document for a merchant by key (for not-yet-issued internal docs). */
  adminDocumentHtmlByKey: async (merchantId: string, key: string): Promise<string> => {
    const token = getToken();
    const res = await fetch(
      `${BASE}/admin/documents/${encodeURIComponent(merchantId)}/${encodeURIComponent(key)}/render`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!res.ok) throw new ApiError(res.status, 'document_failed', 'Документът не можа да се зареди.');
    return res.text();
  },
  saveMerchantDocumentDetails: (
    merchantId: string,
    body: { tax_id?: string; company_registration_no?: string; dba?: string; representative_title?: string },
  ) => post<{ ok: true }>(`/admin/merchants/${encodeURIComponent(merchantId)}/document-details`, body),

  // --- Onboarding / KYC review ---
  mandatoryDocuments: () =>
    request<{ all: Array<{ key: string; title: string }>; mandatory: string[] }>('/admin/mandatory-documents'),
  saveMandatoryDocuments: (keys: string[]) =>
    post<{ ok: true }>('/admin/mandatory-documents', { keys }),
  onboardingEnforcement: () =>
    request<{ enabled: boolean }>('/admin/onboarding-enforcement'),
  setOnboardingEnforcement: (enabled: boolean) =>
    post<{ ok: true }>('/admin/onboarding-enforcement', { enabled }),
  onboardingQueue: () =>
    request<{ pending: number; merchants: OnboardingRow[] }>('/admin/onboarding'),
  merchantOnboarding: (merchantId: string) =>
    request<MerchantOnboarding>(`/admin/merchants/${encodeURIComponent(merchantId)}/onboarding`),
  kycDocumentUrl: (merchantId: string, docId: string) =>
    `${BASE}/admin/merchants/${encodeURIComponent(merchantId)}/kyc-documents/${encodeURIComponent(docId)}`,
  approveOnboarding: (merchantId: string) =>
    post<{ ok: true }>(`/admin/merchants/${encodeURIComponent(merchantId)}/onboarding/approve`),
  rejectOnboarding: (merchantId: string, reason: string) =>
    post<{ ok: true }>(`/admin/merchants/${encodeURIComponent(merchantId)}/onboarding/reject`, { reason }),
  merchants: (params: { status?: string; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.status && params.status !== 'all') q.set('status', params.status);
    if (params.search) q.set('search', params.search);
    return request<{ data: Merchant[] }>(`/admin/merchants?${q}`);
  },
  transactions: (state?: string) =>
    request<{
      data: Array<{
        id: string;
        amount: number;
        currency: string;
        status: string;
        created_at: string;
        merchant: { id: string; name: string };
        application_fee: number;
      }>;
    }>(`/admin/transactions?${new URLSearchParams(state && state !== 'all' ? { state } : {})}`),
  alerts: (status = 'OPEN') =>
    request<{
      data: Array<{
        id: string;
        kind: string;
        severity: string;
        subject: string;
        detail: string;
        status: string;
        created_at: string;
      }>;
    }>(`/admin/alerts?status=${status}`),
  devices: () => request<{ data: Device[] }>('/admin/devices'),

  /**
   * Картовите терминали на цялата платформа.
   *
   * Търговецът вижда своите телефони в собствения си панел. Тук се вижда всичко
   * наведнъж - което е единственият начин да се отговори на въпроса „този
   * апарат регистриран ли е и при друг бизнес“, защото отговорът се чете по
   * `install_id` между наематели.
   */
  terminals: (params: { merchant_id?: string; status?: string; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.merchant_id) q.set('merchant_id', params.merchant_id);
    if (params.status && params.status !== 'all') q.set('status', params.status);
    if (params.search) q.set('search', params.search);
    return request<{ data: AdminTerminal[]; merchants: TerminalMerchant[]; pending_total: number }>(`/admin/terminals?${q}`);
  },

  /** Одитните записи за един терминал - кой го е регистрирал и кой го е отнел. */
  terminalHistory: (id: string) => request<TerminalHistory>(`/admin/terminals/${id}/history`),

  /**
   * Всичко за едно чакащо сдвояване, за да реши администраторът: устройството,
   * търговецът, и адресът, който Stripe е проверил при верификацията. Адресът е
   * смисълът на прегледа - одобрението казва „този апарат е на този бизнес“, а
   * честното сравнение е спрямо адреса, който Stripe вече е потвърдил.
   */
  terminalReview: (id: string) => request<TerminalReview>(`/admin/terminals/${id}/review`),

  /**
   * Approve or reject a pending pairing directly, gated by the `terminals.approve`
   * permission and the admin session rather than a separate signing device. The
   * platform owner asked for terminal review to be a plain button.
   */
  /**
   * Override a payment's status by hand, and the states it may move to. The
   * server only allows valid transitions and audits every change; it moves no
   * money. Used by the inline status editor on payments and the transactions list.
   */
  paymentStatusOptions: (id: string) =>
    request<{ current: string; options: string[] }>(`/admin/payments/${id}/status-options`),
  setPaymentStatus: (id: string, state: string) =>
    post<{ ok: boolean; state: string }>(`/admin/payments/${id}/status`, { state }),

  approveTerminal: (id: string) => post<{ ok: boolean }>(`/admin/terminals/${id}/approve`, {}),
  rejectTerminal: (id: string, reason: string) =>
    post<{ ok: boolean }>(`/admin/terminals/${id}/reject`, { reason }),

  /**
   * Merchant applications - the public front door. Listing, and the three
   * review actions. Approval is what creates the account and its owner login,
   * so it is gated by the `applications.review` permission on the server.
   */
  listApplications: (status?: string) =>
    request<{ applications: MerchantApplication[]; pending: number }>(
      `/admin/applications${status ? `?status=${encodeURIComponent(status)}` : ''}`,
    ),
  approveApplication: (id: string) =>
    post<{ ok: boolean; merchant_id: string }>(`/admin/applications/${id}/approve`, {}),
  requestInfoApplication: (id: string, message: string) =>
    post<{ ok: boolean }>(`/admin/applications/${id}/request-info`, { message }),
  rejectApplication: (id: string, reason?: string) =>
    post<{ ok: boolean }>(`/admin/applications/${id}/reject`, { reason: reason ?? undefined }),

  // --- Fee-waiver packages (subscriptions) ---
  listSubscriptionApplications: (status?: string) =>
    request<{ applications: SubscriptionApplicationRow[]; pending: number }>(
      `/admin/subscription-applications${status ? `?status=${encodeURIComponent(status)}` : ''}`,
    ),
  subscriptionApplication: (id: string) =>
    request<{ application: SubscriptionApplicationRow; analysis: SubscriptionAnalysis | null }>(
      `/admin/subscription-applications/${id}`,
    ),
  approveSubscription: (id: string, amount: number) =>
    post<{ ok: boolean; subscription_id: string; pay_url: string; charge_id: string }>(
      `/admin/subscription-applications/${id}/approve`,
      { amount },
    ),
  requestInfoSubscription: (id: string, message: string) =>
    post<{ ok: boolean }>(`/admin/subscription-applications/${id}/request-info`, { message }),
  rejectSubscription: (id: string, reason?: string) =>
    post<{ ok: boolean }>(`/admin/subscription-applications/${id}/reject`, { reason: reason ?? undefined }),
  listSubscriptions: (status?: string) =>
    request<{ subscriptions: MerchantSubscriptionRow[] }>(
      `/admin/subscriptions${status ? `?status=${encodeURIComponent(status)}` : ''}`,
    ),
  cancelSubscription: (id: string, reason?: string) =>
    post<{ ok: boolean }>(`/admin/subscriptions/${id}/cancel`, { reason: reason ?? undefined }),

  audit: (subject?: string) =>
    request<AuditPage>(`/admin/audit?${new URLSearchParams(subject ? { subject } : {})}`),
  system: () =>
    request<{
      stripe_events: { total: number; failed: number };
      merchant_webhooks: { total: number; exhausted: number };
      audit_chain: { valid: boolean; entries: number; brokenAt?: number; reason?: string };
      reconciliation_runs: Array<{
        id: string;
        started_at: string;
        status: string;
        checked: number;
        discrepancies: number;
        escalated: number;
      }>;
    }>('/admin/system'),

  registerDevice: (body: { user_id: string; name: string; device_type: string; os?: string; public_key_pem: string }) =>
    post<{ device_id: string; key_id: string }>('/admin/devices', body),

  // --- client certificates ---
  //
  // The credential that gets a person to this console at all. Issued here,
  // installed in the operating system keychain, revocable from the list.

  certificates: () => request<CertificatePage>('/admin/certificates'),

  issueCertificate: (body: {
    user_id: string;
    full_name: string;
    email: string;
    machine_label: string;
    validity_class: ValidityClass;
    validity_days?: number;
    note?: string;
    /** A passphrase the administrator chose. Omit to have one generated. At least 12 characters. */
    passphrase?: string;
  }) => post<IssuedCertificate>('/admin/certificates', body),

  revokeCertificate: (id: string, reason: string) =>
    post<{ ok: boolean; sessions_terminated: number }>(`/admin/certificates/${id}/revoke`, {
      reason,
    }),

  /** Every privileged state change goes through here, signed. */
  command: (command: ControlCommand, signature: string) =>
    post<{ ok: boolean; target: string; transition: string; new_version: number }>('/admin/commands', {
      command,
      signature,
    }),

  reconcile: () =>
    post<{ runId: string; checked: number; discrepancies: number; autoCorrected: number; escalated: number }>(
      '/admin/reconcile',
    ),

  payment: (id: string) =>
    request<{
      payment: {
        id: string; amount: number; currency: string; status: string;
        merchant_reference: string | null; description: string | null;
        created_at: string; paid_at: string | null; amount_refunded: number;
        refund_status: string; dispute_status: string;
        stripe_payment_intent_id: string | null; connected_account_id: string | null;
        application_fee: number; expires_at: string | null; version: number;
      };
      merchant: { id: string; name: string; email: string; status: string; stripeAccountId: string | null };
      attempts: Array<{
        id: string; sequence: number; status: string; payment_method_type: string | null;
        failure_code: string | null; decline_code: string | null; failure_message: string | null;
        stripe_charge_id: string | null; created_at: string; settled_at: string | null;
      }>;
      links: Array<{ id: string; created_at: string; expires_at: string; expired: boolean; revoked: boolean }>;
      refunds: Array<{
        id: string; amount: number; currency: string; status: string | null;
        reason: string | null; created: string; requested_by: string | null;
      }>;
      alerts: Array<{ id: string; kind: string; severity: string; detail: string; status: string; created_at: string }>;
      timeline: Array<{ at: string; action: string; actor: string; data: unknown }>;
    }>(`/admin/payments/${id}`),

  paymentLink: (id: string, mode: 'renew' | 'reissue') =>
    post<{ mode: string; url: string | null; expires_at: string }>(`/admin/payments/${id}/link`, { mode }),

  revokePaymentLink: (id: string, linkId: string) =>
    post<{ ok: boolean }>(`/admin/payments/${id}/links/${linkId}/revoke`),

  merchant: (id: string) =>
    request<{
      merchant: Merchant & {
        currency: string;
        details_submitted: boolean;
        fee_mode: string;
        subscription_tier: string;
        platform_owned: boolean;
        contact: { phone: string | null; address: string | null };
      };
      /** What a new merchant would be charged, so a custom rate is visible as one. */
      platform_default_fee: { percent_bps: number; fixed: number };
      fee_model: FeeModel;
      stripe_account: {
        id: string;
        charges_enabled: boolean;
        payouts_enabled: boolean;
        details_submitted: boolean;
        default_currency: string | null;
        country: string | null;
        capabilities: Record<string, string>;
        requirements: {
          currently_due: string[];
          eventually_due: string[];
          past_due: string[];
          disabled_reason: string | null;
          current_deadline: string | null;
        };
      } | null;
      stripe_configured: boolean;
      currency_advice: MerchantCurrencyAdvice;
      /** Metadata only. The bytes come from `merchantLogoImage`. */
      logo: MerchantLogo;
      users: Array<{ id: string; name: string; email: string; role: string; status: string }>;
      counts: { payments: number; api_keys: number; webhook_endpoints: number; open_alerts: number };
      totals: { volume: number; fees: number; refunded: number; settled_payments: number };
      recent_payments: Array<{ id: string; amount: number; currency: string; status: string; created_at: string }>;
      timeline: Array<{ at: string; action: string; actor: string; data: unknown }>;
    }>(`/admin/merchants/${id}`),

  machinePayments: (merchantId: string) =>
    request<{
      status: string;
      approved: boolean;
      merchant: { id: string; name: string; active: boolean; stripe_account_id: string | null; charges_enabled: boolean; payouts_enabled: boolean };
      protocols: { mpp: boolean; x402: boolean };
      x402: { pay_to: string | null; network: string | null; token: string | null };
      rejection_reason: string | null;
    }>(`/admin/merchants/${encodeURIComponent(merchantId)}/machine-payments`),
  machinePaymentRequests: () => request<{ data: Array<{ merchant_id: string; merchant: { id: string; name: string; email: string; status: string; stripeAccountId: string | null; chargesEnabled: boolean; payoutsEnabled: boolean }; status: string; protocols: { mpp: boolean; x402: boolean }; x402: { configured: boolean; network: string | null }; requested_at: string | null }> }>('/admin/machine-payments/requests'),
  approveMachinePayments: (merchantId: string, body: { mpp_enabled: boolean; x402_enabled: boolean; x402_pay_to?: string | null; x402_network?: string | null; x402_token?: string | null }) => post(`/admin/merchants/${encodeURIComponent(merchantId)}/machine-payments/approve`, body),
  rejectMachinePayments: (merchantId: string, reason?: string) => post(`/admin/merchants/${encodeURIComponent(merchantId)}/machine-payments/reject`, { reason }),
  revokeMachinePayments: (merchantId: string, reason?: string) => post(`/admin/merchants/${encodeURIComponent(merchantId)}/machine-payments/revoke`, { reason }),

  queue: () =>
    request<{
      data: Array<{
        merchant: { id: string; name: string; email: string; status: string; version: number };
        stripe_account_id: string | null;
        charges_enabled: boolean;
        payouts_enabled: boolean;
        requirements_due: string[];
        payments: number;
        created_at: string;
        reasons: string[];
        reason_codes: string[];
        priority: string;
      }>;
    }>('/admin/queue'),

  revenue: (days = 30) =>
    request<{
      period_days: number;
      /** One row per currency. These are not addable. */
      total: Array<{ currency: string; volume: number; fees: number; refunded: number; payments: number }>;
      by_merchant: Array<{
        key: string;
        label: string;
        payments: number;
        volume: Array<{ currency: string; amount: number }>;
        fees: Array<{ currency: string; amount: number }>;
      }>;
      by_method: Array<{
        key: string;
        label: string;
        payments: number;
        volume: Array<{ currency: string; amount: number }>;
        fees: Array<{ currency: string; amount: number }>;
      }>;
    }>(`/admin/revenue?days=${days}`),

  refundsOversight: () =>
    request<{
      refunds: Array<{
        payment_id: string;
        merchant: { id: string; name: string };
        amount: number;
        amount_refunded: number;
        currency: string;
        status: string;
        fully_refunded: boolean;
        updated_at: string;
      }>;
      disputes: Array<{
        payment_id: string;
        merchant: { id: string; name: string };
        amount: number;
        currency: string;
        dispute_status: string;
        status: string;
        updated_at: string;
      }>;
    }>('/admin/refunds'),

  payoutOversight: () =>
    request<{
      stripe_configured: boolean;
      data: Array<{
        id: string;
        merchant: { id: string; name: string };
        amount: number;
        currency: string;
        status: string;
        method: string;
        arrival_date: string;
        created: string;
        failure_code: string | null;
        failure_message: string | null;
      }>;
      failed?: number;
      note?: string;
    }>('/admin/payouts'),

  /**
   * `scope` decides which population is being looked at: the platform's own
   * staff, or the merchants' people. They are administered for different
   * reasons and the console never shows them in one list.
   */
  users: (params: { search?: string; scope?: 'staff' | 'merchant' | 'all' } = {}) => {
    const q = new URLSearchParams();
    if (params.search) q.set('search', params.search);
    if (params.scope && params.scope !== 'all') q.set('scope', params.scope);
    return request<{
      data: Array<{
        id: string;
        name: string;
        email: string;
        role: string;
        status: string;
        merchant: { id: string; name: string } | null;
        sessions: number;
        devices: number;
        created_at: string;
      }>;
    }>(`/admin/users?${q}`);
  },

  createMerchant: (body: {
    name: string;
    email: string;
    country: string;
    currency: string;
    /** Decided here or never. See `FeeModel`. */
    fee_payer: 'account' | 'application';
    contact_phone?: string;
    contact_address?: string;
  }) =>
    post<{
      merchant_id: string;
      fee_payer: string;
      invite: { id: string; url: string; expires_at: string };
    }>('/admin/merchants', body),

  /**
   * What the platform earned from each merchant over a period.
   *
   * A view, not an invoice. The money has already been collected by Stripe out
   * of each charge; these are the figures the owner's separate WYOCS system
   * raises its invoices from, and no number is allocated here.
   */
  feeBilling: (query: { from: string; to: string; merchant_id?: string }) => {
    const q = new URLSearchParams({ from: query.from, to: query.to });
    if (query.merchant_id) q.set('merchant_id', query.merchant_id);
    return request<FeeBillingStatement>(`/admin/fee-billing?${q}`);
  },

  featureRequests: (status = 'all') =>
    request<{
      data: Array<{
        id: string;
        merchant: { id: string; name: string; email: string; status: string };
        feature: string;
        feature_label: string;
        approval_rationale: string | null;
        condition_schema: Array<{ key: string; label: string; type: string; default: number }>;
        status: string;
        justification: string;
        intended_use: string;
        expected_volume: string | null;
        created_at: string;
        decided_by: string | null;
        decided_at: string | null;
        decision_note: string | null;
        conditions: Record<string, unknown> | null;
        messages: Array<{ id: string; author_role: string; body: string; created_at: string }>;
      }>;
    }>(`/admin/feature-requests?status=${status}`),

  askFeatureRequest: (id: string, question: string) =>
    post<{ ok: boolean }>(`/admin/feature-requests/${id}/ask`, { question }),

  decideFeatureRequest: (
    id: string,
    body: { approve: boolean; note: string; conditions?: Record<string, unknown> },
  ) => post<{ ok: boolean }>(`/admin/feature-requests/${id}/decide`, body),

  /**
   * Merchants asking for an account detail to be changed.
   *
   * `live_value` and `stale` are computed by the server at read time rather than
   * stored: what matters is whether the field has moved since the merchant asked,
   * and only a fresh read can answer that.
   */
  detailRequests: (status = 'SUBMITTED') =>
    request<{
      data: Array<{
        id: string;
        merchant: { id: string; name: string; email: string; status: string };
        field: string;
        field_label: string;
        status: string;
        current_value: string;
        requested_value: string;
        live_value: string | null;
        stale: boolean;
        note: string;
        reason: string | null;
        requested_by: string;
        created_at: string;
        decided_by: string | null;
        decided_at: string | null;
        decision_note: string | null;
        applied_value: string | null;
      }>;
    }>(`/admin/detail-requests?status=${status}`),

  decideDetailRequest: (
    id: string,
    body: { approve: boolean; note: string; acknowledged_current_value?: string },
  ) => post<{ ok: boolean; applied: boolean }>(`/admin/detail-requests/${id}/decide`, body),

  approvals: (status = 'PENDING') =>
    request<{
      data: Array<{
        id: string;
        target: string;
        transition: string;
        params: Record<string, unknown>;
        requested_by: string;
        requested_by_name: string;
        requested_at: string;
        expires_at: string;
        expired: boolean;
        status: string;
        approved_by_name: string | null;
        rejection_reason: string | null;
        why: string | null;
      }>;
    }>(`/admin/approvals?status=${status}`),

  settings: () =>
    request<{
      data: Array<{
        key: string;
        label: string;
        description: string;
        type: 'number' | 'boolean' | 'string';
        default: unknown;
        sensitive: boolean;
        value: unknown;
        version: number;
        updatedBy: string | null;
        updatedAt: string | null;
      }>;
    }>('/admin/settings'),

  // --- Stripe credentials ---

  credentials: () => request<CredentialStatus>('/admin/credentials'),
  saveCredentials: (values: Record<string, string>) =>
    post<{ restart_required: boolean; account: string | null }>('/admin/credentials', values),

  // --- access control ---

  accessCatalogue: () =>
    request<{ roles: RoleDefinition[]; permissions: PermissionDefinition[] }>('/admin/access/catalogue'),
  staff: () => request<{ data: StaffMember[] }>('/admin/access/staff'),
  createStaff: (body: { name: string; email: string; role: string; permissions: string[] }) =>
    post<{ id: string; email: string; password: string }>('/admin/access/staff', body),
  setStaffRole: (id: string, role: string) =>
    post<{ ok: boolean }>(`/admin/access/staff/${id}/role`, { role }),
  setStaffPermissions: (id: string, permissions: string[]) =>
    post<{ ok: boolean }>(`/admin/access/staff/${id}/permissions`, { permissions }),
  setStaffPassword: (id: string, password: string) =>
    post<{ sessions_terminated: number }>(`/admin/access/staff/${id}/password`, { password }),
  updateStaff: (id: string, body: { name?: string; email?: string }) =>
    request<{ ok: boolean }>(`/admin/access/staff/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  // --- fees and liability ---

  fees: (merchantId?: string) =>
    request<{
      rates: {
        processorPercentBps: number;
        processorFixed: number;
        platformPercentBps: number;
        platformFixed: number;
        combinedPercentBps: number;
        combinedFixed: number;
        currency: string;
      };
      combined_label: string;
      liability: {
        refunds: string;
        disputes: string;
        negativeBalance: string;
        platformFeeRefunded: boolean;
        platformFeeRefundedOnDispute: boolean;
        notes: string[];
      };
    }>(`/admin/fees${merchantId ? `?merchant_id=${merchantId}` : ''}`),

  paymentFees: (id: string) =>
    request<{
      amount: number;
      currency: string;
      settlementCurrency: string | null;
      converted: boolean;
      stripeFee: number | null;
      platformFee: number | null;
      platformFeeCharged: number;
      merchantNet: number | null;
      breakdown: Array<{ type: string; description: string | null; amount: number }>;
      authoritative: boolean;
    }>(`/admin/payments/${id}/fees`),

  // --- fee catalogue, analytics, tariff calculator, plans, emergency mode ---

  /**
   * Estimated-vs-actual fee drift and platform margin over a window, computed
   * from the captured fee snapshots. Money figures in the aggregate buckets sum
   * across currencies; the per-currency table is the honest breakdown.
   */
  feeAnalytics: (sinceDays = 90) =>
    request<FeeAnalytics>(`/admin/fee-analytics?since_days=${sinceDays}`),

  feeCatalogVersions: () => request<{ versions: FeeCatalogVersion[] }>('/admin/fee-catalog/versions'),

  feeCatalogActive: () =>
    request<{ active: { id: string; label: string } | null; rules: FeeRule[] }>('/admin/fee-catalog/active'),

  feeCatalogRules: (versionId: string) =>
    request<{ rules: FeeRule[] }>(`/admin/fee-catalog/versions/${versionId}/rules`),

  publishFeeCatalogVersion: (versionId: string) =>
    post<{ ok: boolean }>(`/admin/fee-catalog/versions/${versionId}/publish`),

  rollbackFeeCatalogVersion: (versionId: string) =>
    post<{ ok: boolean; versionId: string }>(`/admin/fee-catalog/versions/${versionId}/rollback`),

  /**
   * The admin tariff calculator. Every number is rendered from the response: the
   * engine is the same one that prices a real charge, so a quote here and a live
   * charge are never computed two different ways.
   */
  feeEstimate: (body: FeeEstimateInput) => post<FeeEstimate>('/admin/fee-catalog/estimate', body),

  feeCompare: (body: FeeCompareInput) =>
    post<{ results: Array<{ method: string; estimate: FeeEstimate }> }>('/admin/fee-catalog/compare', body),

  feeRecommend: (body: FeeCompareInput) => post<FeeRecommendation>('/admin/fee-catalog/recommend', body),

  feeBreakEven: (body: FeeBreakEvenInput) => post<FeeBreakEven>('/admin/fee-catalog/break-even', body),

  plans: () => request<{ plans: Plan[] }>('/admin/plans'),

  merchantPlan: (merchantId: string) =>
    request<{ assignment: PlanAssignment | null }>(`/admin/merchants/${merchantId}/plan`),

  assignMerchantPlan: (merchantId: string, planId: string, promotionId?: string | null) =>
    post<{ assignmentId: string; feeApplied: boolean }>(`/admin/merchants/${merchantId}/plan`, {
      plan_id: planId,
      ...(promotionId ? { promotion_id: promotionId } : {}),
    }),

  emergencyMode: () => request<{ current: string; options: string[] }>('/admin/emergency-mode'),

  // --- payment page advertising ---

  /**
   * Banners are sold by the platform and shown under the payment form.
   *
   * The whole feature is self-hosted: the image bytes live in our database, the
   * destination is a plain https URL, and there is no ad network, no external
   * tag and no remote asset anywhere in it. That is not a preference - the
   * payment page's Content-Security-Policy is what keeps the platform inside
   * PCI DSS SAQ A. See compliance/payment-page-advertising.md.
   */
  banners: (filter: { active?: boolean; merchant_id?: string } = {}) => {
    const q = new URLSearchParams();
    if (filter.active !== undefined) q.set('active', String(filter.active));
    if (filter.merchant_id) q.set('merchant_id', filter.merchant_id);
    return request<{
      data: Banner[];
      accepted_types: string[];
      max_bytes: number;
      /**
       * The accepted pixel dimensions, from the server that enforces them.
       *
       * Stated on the upload screen before a file is chosen. It comes from here
       * rather than from a constant in the component so that adding a size
       * cannot leave the screen advertising the old list.
       */
      accepted_sizes: BannerSize[];
      max_total_pixels: number;
    }>(`/admin/banners?${q}`);
  },
  banner: (id: string) =>
    request<Banner & { daily: Array<{ day: string; impressions: number; clicks: number }> }>(
      `/admin/banners/${id}`,
    ),
  createBanner: (body: BannerInput & { image: string }) => post<{ id: string }>('/admin/banners', body),
  updateBanner: (id: string, body: Partial<BannerInput> & { image?: string }) =>
    request<{ id: string }>(`/admin/banners/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  setBannerActive: (id: string, active: boolean) =>
    post<{ id: string; active: boolean }>(`/admin/banners/${id}/active`, { active }),

  /**
   * The creative itself.
   *
   * A blob rather than a `src` on the API, because the bytes need the bearer
   * token that a plain <img> would not send. The console renders it from an
   * object URL, which its own policy allows - `img-src` there includes `blob:`.
   * The payment page never touches this route: it receives the bytes inline
   * over the call it already makes, for the reasons written out in
   * services/banners.ts.
   */
  bannerImage: async (id: string): Promise<Blob> => {
    const token = getToken();
    const response = await fetch(`${BASE}/admin/banners/${id}/image`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      throw new ApiError(response.status, 'image_failed', 'Изображението не може да се зареди.');
    }
    return response.blob();
  },

  // --- merchant branding ---

  /**
   * A merchant's logo, as their own customers see it on the payment page.
   *
   * The merchant uploads their own; the platform can look at it, replace it and
   * take it down. Taking it down is the point: a logo is the only thing on that
   * page a merchant chooses, and somebody has to be able to remove one that
   * turns out to be another company's mark.
   */
  merchantLogoImage: async (id: string): Promise<Blob> => {
    const token = getToken();
    const response = await fetch(`${BASE}/admin/merchants/${id}/logo/image`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      throw new ApiError(response.status, 'logo_failed', 'Логото не може да се зареди.');
    }
    return response.blob();
  },
  setMerchantLogo: (id: string, image: string) =>
    post<MerchantLogo>(`/admin/merchants/${id}/logo`, { image }),
  removeMerchantLogo: (id: string, reason?: string) =>
    request<MerchantLogo>(`/admin/merchants/${id}/logo`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    }),

  // --- EU platform reporting (DAC7) ---
  //
  // Council Directive (EU) 2021/514. Read endpoints need `dac7.view`; producing
  // a return needs `dac7.manage`, because the file carries dates of birth and
  // tax numbers for named people.

  dac7: (year: number) =>
    request<{
      year: number;
      deadline: string;
      operator: Dac7Operator;
      counts: { surveyed: number; reportable: number; excluded: number; needs_decision: number };
      sellers: Dac7SellerRow[];
      guidance: {
        relevant_activities: Array<{ key: string; label: string; description: string; citation: string }>;
        member_states: string[];
        de_minimis: { activity_count: number; consideration_eur: number; citation: string; note: string };
      };
      blockers: string[];
    }>(`/admin/dac7?year=${year}`),

  dac7Merchant: (id: string, year: number) =>
    request<{
      merchant: { id: string; name: string; email: string; country: string; status: string };
      year: number;
      scope: { inScope: boolean; signals: string[] };
      profile: unknown;
      decision: unknown;
      aggregate: unknown;
      missing: Dac7MissingField[];
      due_diligence_valid: boolean;
      enforcement_due: boolean;
    }>(`/admin/dac7/merchants/${id}?year=${year}`),

  /** Reads the connected account and reports what Stripe would and would not give. */
  dac7Stripe: (id: string) =>
    post<{
      supplied: {
        accountId: string;
        businessType: string | null;
        companyName: string | null;
        firstName: string | null;
        lastName: string | null;
        email: string | null;
        country: string | null;
        taxIdProvided: boolean;
        vatIdProvided: boolean;
        externalAccountLast4: string | null;
        notAvailable: Array<{ field: string; reason: string }>;
      };
    }>(`/admin/dac7/merchants/${id}/stripe`),

  dac7Verify: (id: string, body: { method: string; outcome: 'passed' | 'failed' | 'not_possible'; detail?: string }) =>
    post<{ profile: unknown }>(`/admin/dac7/merchants/${id}/verify`, body),

  dac7Remind: (id: string) => post<{ profile: unknown }>(`/admin/dac7/merchants/${id}/remind`),

  dac7XmlCheck: (year: number) =>
    request<{ year: number; records: number; blockers: string[]; caveats: string[]; bytes: number }>(
      `/admin/dac7/report/xml-check?year=${year}`,
    ),

  /**
   * The return itself, as a file.
   *
   * Fetched with the bearer token rather than opened as a link: a link would put
   * a URL that yields dates of birth and tax numbers into the browser history,
   * and there is no way to attach a header to an anchor.
   */
  dac7Download: async (year: number, format: 'csv' | 'xml'): Promise<Blob> => {
    const token = getToken();
    const response = await fetch(`${BASE}/admin/dac7/report.${format}?year=${year}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      throw new ApiError(response.status, 'report_failed', 'Отчетът не може да се генерира.');
    }
    return response.blob();
  },

  // --- support tickets ---

  tickets: (q: { status?: string; search?: string; merchant_id?: string } = {}) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) if (v) params.set(k, v);
    return request<{ data: TicketSummary[]; counts: Record<string, number> }>(
      `/admin/tickets?${params.toString()}`,
    );
  },
  ticket: (id: string) => request<TicketThread>(`/admin/tickets/${id}`),
  replyToTicket: (
    id: string,
    body: { body: string; internal: boolean; attachments: Array<{ filename: string; content: string }> },
  ) => post<{ id: string; status: string }>(`/admin/tickets/${id}/reply`, body),
  setTicketStatus: (id: string, status: 'RESOLVED' | 'CLOSED' | 'OPEN', note?: string) =>
    post<{ status: string }>(`/admin/tickets/${id}/status`, { status, note }),
  updateTicket: (id: string, change: { priority?: string; category?: string; assigned_to?: string | null }) =>
    request<{ ok: boolean }>(`/admin/tickets/${id}`, { method: 'PATCH', body: JSON.stringify(change) }),

  // --- отчети и експорт ---

  /** Приходи на платформата за период - по месеци, по търговци и по метод. */
  revenueReport: (query: RevenueQuery) =>
    request<ReportDocument>(`/admin/reports/revenue?${revenueParams(query, 'json')}`),

  revenueReportFile: (query: RevenueQuery, format: 'csv' | 'xlsx', table?: string) =>
    reportBlob(`/admin/reports/revenue?${revenueParams(query, format)}`, table),

  revenueReportHtml: (query: RevenueQuery) =>
    reportText(`/admin/reports/revenue?${revenueParams(query, 'html')}`),

  /**
   * Одитният дневник като отчет.
   *
   * Проверката на хеш-веригата се прави на сървъра и резултатът ѝ стои на
   * лицевата страна на документа. Панелът не решава нищо за достоверността -
   * той показва това, което сървърът е установил.
   */
  auditReport: (query: AuditQuery) =>
    request<ReportDocument>(`/admin/reports/audit?${auditParams(query, 'json')}`),

  auditReportFile: (query: AuditQuery, format: 'csv' | 'xlsx', table?: string) =>
    reportBlob(`/admin/reports/audit?${auditParams(query, format)}`, table),

  auditReportHtml: (query: AuditQuery) =>
    reportText(`/admin/reports/audit?${auditParams(query, 'html')}`),

  auditActions: (chain?: string) =>
    request<{ data: Array<{ action: string; count: number }> }>(
      `/admin/reports/audit/actions${chain ? `?chain=${encodeURIComponent(chain)}` : ''}`,
    ),

  /** Attachment bytes need the bearer token, so they cannot be a plain link. */
  ticketAttachment: async (id: string): Promise<Blob> => {
    const token = getToken();
    const response = await fetch(`${BASE}/admin/tickets/attachments/${id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      throw new ApiError(response.status, 'attachment_failed', 'Прикаченият файл не може да се зареди.');
    }
    return response.blob();
  },
};

// ---------------------------------------------------------------------------
// Отчети
// ---------------------------------------------------------------------------

export interface RevenueQuery {
  /** Календарни дни, включително и двата края. Сървърът ги чете в UTC. */
  from: string;
  to: string;
  /** За колко плащания се чете реалната такса на Stripe. */
  feeLimit?: number;
}

export interface AuditQuery {
  /** Ден, месец или година около една дата - или изричен период. */
  scope?: 'day' | 'month' | 'year';
  on?: string;
  from?: string;
  to?: string;
  /** Точно действие, или префикс като `payment.` */
  action?: string;
  chain?: string;
}

function revenueParams(query: RevenueQuery, format: string): string {
  const params = new URLSearchParams({ from: query.from, to: query.to, format });
  if (query.feeLimit) params.set('fee_limit', String(query.feeLimit));
  return params.toString();
}

function auditParams(query: AuditQuery, format: string): string {
  const params = new URLSearchParams({ format });
  for (const [key, value] of Object.entries(query)) {
    if (typeof value === 'string' && value.length > 0) params.set(key, value);
  }
  return params.toString();
}

/**
 * Файловете се теглят с токена на сесията, а не като обикновен линк.
 *
 * Един `<a href>` не носи заглавка Authorization, а адрес, който връща целия
 * оборот на платформата, няма работа в историята на браузъра.
 */
async function reportBlob(path: string, table?: string): Promise<Blob> {
  const token = getToken();
  const response = await fetch(`${BASE}${path}${table ? `&table=${encodeURIComponent(table)}` : ''}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new ApiError(response.status, 'report_failed', 'Отчетът не може да бъде генериран.');
  }
  return response.blob();
}

async function reportText(path: string): Promise<string> {
  const token = getToken();
  const response = await fetch(`${BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new ApiError(response.status, 'report_failed', 'Отчетът не може да бъде генериран.');
  }
  return response.text();
}

/**
 * Записване на файл, който браузърът вече държи.
 *
 * Object URL вместо линк към API-то, за да се запишат байтовете, изтеглени със
 * сесийния токен, вместо браузърът да ги поиска отново без него. Отменя се
 * веднага - това е дръжка към финансови данни в паметта и няма причина да
 * надживява кликването.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Client certificates
// ---------------------------------------------------------------------------

export type ValidityClass = 'STANDARD' | 'LONG_LIVED';

export interface AdminCertificate {
  id: string;
  user: { id: string; name: string; email: string; role: string; status: string };
  common_name: string;
  email: string;
  machine_label: string;
  serial: string;
  fingerprint_sha256: string;
  /** STANDARD unless somebody deliberately chose otherwise and said why. */
  validity_class: ValidityClass;
  issuance_note: string | null;
  issued_at: string;
  expires_at: string;
  expired: boolean;
  days_remaining: number;
  status: string;
  issued_by: string;
  revoked_at: string | null;
  revoked_by: string | null;
  revoked_reason: string | null;
}

export interface CertificatePage {
  data: AdminCertificate[];
  ca: {
    present: boolean;
    directory: string;
    subject: string | null;
    expires_at: string | null;
    /** False means the CA private key is readable by another account on the box. */
    key_permissions_ok: boolean | null;
    /** What a long-lived certificate would get today. Null when there is no CA. */
    long_lived_days: number | null;
  };
  mtls_mode: 'off' | 'optional' | 'required';
  default_validity_days: number;
  min_validity_days: number;
  max_validity_days: number;
}

/**
 * The one and only time the bundle and its passphrase exist outside a keychain.
 *
 * Neither is stored anywhere. If the passphrase is lost between this response
 * and the import dialog, the certificate is revoked and another is issued.
 */
export interface IssuedCertificate {
  id: string;
  common_name: string;
  email: string;
  machine_label: string;
  serial: string;
  fingerprint_sha256: string;
  validity_class: ValidityClass;
  issued_at: string;
  expires_at: string;
  filename: string;
  pkcs12_base64: string;
  /** The bundle passphrase. Empty when the administrator chose it, since then it is not echoed. */
  passphrase: string;
  /** True when the administrator set the passphrase themselves rather than it being generated. */
  passphrase_chosen: boolean;
}

/** One accepted upload size: a named slot, at 1x or at 2x. */
export interface BannerSize {
  slot: string;
  label: string;
  scale: number;
  width: number;
  height: number;
}

export interface Banner {
  id: string;
  name: string;
  advertiser: string;
  destination_url: string;
  alt_text: string;
  mime_type: string;
  size: number;
  sha256: string;
  /**
   * The named format this creative was accepted as, and the dimensions read out
   * of its own header. Null on a banner uploaded before the sizes were defined,
   * which the console shows as an unknown format and asks to have replaced.
   */
  slot: string | null;
  width: number | null;
  height: number | null;
  active: boolean;
  /** Null means every merchant's payment page; a value targets one merchant. */
  merchant_id: string | null;
  starts_at: string | null;
  ends_at: string | null;
  weight: number;
  created_by: string;
  created_at: string;
  updated_at: string;
  impressions: number;
  clicks: number;
  /**
   * The same two counts over the recent window the share is computed against.
   *
   * Lifetime totals are the wrong thing to judge a rotation by, because they
   * include every week the set of running banners was a different shape.
   */
  recent_impressions: number;
  recent_clicks: number;
  /** Measured share of its own rotation pool over that window, 0 to 1. */
  share: number | null;
  /** The share its weight asks for right now, 0 to 1. Null when not eligible. */
  expected_share: number | null;
  /** Whether it would be in the running if a payment page asked this second. */
  eligible: boolean;
  image_url: string;
}

export interface BannerInput {
  name: string;
  advertiser: string;
  destination_url: string;
  alt_text: string;
  merchant_id?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  weight?: number;
}

/**
 * Which side pays Stripe for one merchant's account.
 *
 * Sent by the server as a fact, never as an option. The choice belongs to
 * account creation and Stripe does not accept `controller` on an account
 * update, so there is nothing here for a control to change - the console
 * states the model and says so.
 */
export interface FeeModel {
  payer: 'account' | 'application';
  stripe_bills: 'merchant' | 'platform';
  can_be_free_of_all_fees: boolean;
  files_1099: 'stripe' | 'platform';
  /** True once a connected account exists, which is the point of no return. */
  locked: boolean;
}

/** One currency's worth of trading. Lines are never added across currencies. */
export interface FeeLine {
  currency: string;
  payments: number;
  volume: number;
  feesEarned: number;
  refunded: number;
}

/**
 * A per-currency figure folded into dollars, carrying the rate that did it.
 *
 * `missingRates` are the currencies left out entirely, which is the honest
 * behaviour when no rate can be had - see services/fx.ts.
 */
export interface Converted {
  total: number;
  exact: number;
  estimated: number;
  currency: string;
  applied: Array<{
    currency: string;
    rate: number;
    referenceRate: number | null;
    referenceProvider: string | null;
    feeRate: number | null;
    source: 'stripe' | 'configured';
    amount: number;
    converted: number;
  }>;
  missingRates: string[];
  liveRates: boolean;
}

export interface MerchantFeeStatement {
  merchant: {
    id: string;
    name: string;
    email: string;
    country: string;
    currency: string;
    status: string;
  };
  rule: {
    percentBps: number;
    fixed: number;
    isPlatformDefault: boolean;
    exemptFromPlatformFee: boolean;
  };
  feePayer: string;
  lines: FeeLine[];
  byMonth: Array<{ month: string; lines: FeeLine[] }>;
  earned: Converted;
  /** A reconciliation key for an external system. Not an invoice number. */
  externalKey: string;
}

export interface FeeBillingStatement {
  period: { from: string; to: string; days: number };
  reportingCurrency: string;
  merchants: MerchantFeeStatement[];
  totalLines: FeeLine[];
  totalEarned: Converted;
  excludedCurrencies: string[];
  caveats: string[];
}

export interface MerchantLogo {
  present: boolean;
  mime_type: string | null;
  size: number | null;
  sha256: string | null;
  updated_at: string | null;
  prompted: boolean;
}

export interface TicketSummary {
  id: string;
  number: string;
  merchant: { id: string; name: string } | null;
  subject: string;
  category: string;
  priority: string;
  status: string;
  messages: number;
  created_at: string;
  last_message_at: string;
}

export interface TicketAttachmentMeta {
  id: string;
  filename: string;
  mime_type: string;
  size: number;
  inline: boolean;
}

export interface TicketMessage {
  id: string;
  author_name: string;
  author_side: string;
  body: string;
  internal: boolean;
  created_at: string;
  attachments: TicketAttachmentMeta[];
}

export interface TicketThread extends Omit<TicketSummary, 'messages' | 'merchant'> {
  merchant: { id: string; name: string; email: string } | null;
  messages: TicketMessage[];
  resolved_at: string | null;
  closed_at: string | null;
}

export interface PermissionDefinition {
  key: string;
  group: string;
  label: string;
  description: string;
  sensitive: boolean;
}

export interface RoleDefinition {
  key: string;
  label: string;
  description: string;
  permissions: string[];
  implicit: boolean;
}

export interface StaffMember {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  extra_permissions: string[];
  effective_count: number;
  can_bypass_approval: boolean;
  devices: number;
  sessions: number;
  permissions_set_by: string | null;
  permissions_set_at: string | null;
  created_at: string;
}

export interface CredentialStatus {
  configured: boolean;
  livemode: boolean;
  applied: boolean;
  fields: Record<string, { present: boolean; hint: string | null; length: number }>;
  webhook_urls: { payments: string; connect: string };
  missing: string[];
}

// ---------------------------------------------------------------------------
// EU platform reporting (DAC7)
// ---------------------------------------------------------------------------

/** Annex V Section III(B)(1): who the return says it comes from. */
export interface Dac7Operator {
  name: string;
  registeredOfficeAddress: string;
  tin: string;
  individualIdentificationNumber: string;
  memberStateOfRegistration: string;
  platformBusinessName: string;
  /** Anything blank, named. A return cannot be filed without all of it. */
  missing: string[];
}

export interface Dac7MissingField {
  field: string;
  label: string;
  citation: string;
  /**
   * `required` blocks the return. `conditional` bites only in a stated case.
   * `where_available` is qualified that way in the Directive, so its absence is
   * correct rather than incomplete - which is why the three must not render
   * alike.
   */
  severity: 'required' | 'conditional' | 'where_available';
  note?: string;
}

export interface Dac7SellerRow {
  merchant_id: string;
  merchant_name: string;
  seller_type: string;
  has_profile: boolean;
  /** Annex V Section I(A)(8). Declared, not inferred - a payment row cannot say. */
  relevant_activities: string[];
  due_diligence_status: string;
  due_diligence_valid: boolean;
  /** Section IV(A)(2): two reminders and 60 days have passed with no answer. */
  enforcement_due: boolean;
  reportable: boolean;
  excluded: boolean;
  exclusion: string | null;
  active_seller: boolean;
  resident_member_states: string[];
  property_member_states: string[];
  reasons: string[];
  /** Non-empty means a person has to decide before the return is filed. */
  undetermined: string[];
  missing: Dac7MissingField[];
  /** One row per currency. Never added together. */
  totals: Array<{ currency: string; consideration: number; feesWithheld: number; activityCount: number }>;
  activity_count: number;
}

// ---------------------------------------------------------------------------
// Fee catalogue, analytics, tariff calculator, plans, emergency mode
// ---------------------------------------------------------------------------

/**
 * One aggregate bucket of the fee analytics. All money is in minor units. The
 * aggregate buckets (`totals`, `byMethod`, `byChannel`, `byRegion`) sum across
 * currencies as the server returns them; `byCurrency` is the honest per-currency
 * split. `estimateDrift` is actual minus estimated - positive means the cost was
 * under-estimated.
 */
export interface FeeAnalyticsBucket {
  key: string;
  count: number;
  grossAmount: number;
  estimatedTotalFee: number;
  actualTotalFee: number;
  processorFee: number;
  platformFee: number;
  merchantNet: number;
  estimateDrift: number;
  platformMargin: number;
}

export interface FeeAnalytics {
  since: string;
  finalisedOnly: true;
  totals: FeeAnalyticsBucket;
  byMethod: FeeAnalyticsBucket[];
  byChannel: FeeAnalyticsBucket[];
  byRegion: FeeAnalyticsBucket[];
  byCurrency: FeeAnalyticsBucket[];
  /** Payments whose actual cost has not been read yet - the estimate still stands. */
  pendingReconciliation: number;
}

/** A published or draft version of the fee catalogue. */
export interface FeeCatalogVersion {
  id: string;
  label: string;
  status: string;
  rules: number;
  effective_from: string;
  effective_until: string | null;
  published_at: string | null;
  created_at: string;
  note: string | null;
}

/** A single priced rule in the catalogue. Money in minor units, rates in bps. */
export interface FeeRule {
  id: string;
  versionId: string;
  provider: string;
  region: string;
  channel: string;
  method: string;
  cardCategory: string | null;
  currency: string | null;
  percentBps: number;
  fixed: number;
  authorizationFee: number;
  minimumFee: number | null;
  maximumFee: number | null;
  fxPercentBps: number;
  instantPayoutPercentBps: number;
  instantPayoutMinimum: number | null;
  instantPayoutMaximum: number | null;
  disputeFee: number;
  refundFee: number;
  note: string | null;
  sourceType: string;
  sourceReference: string | null;
}

export interface FeeEstimateInput {
  /** Minor units. */
  amount: number;
  currency: string;
  country?: string | null;
  channel?: 'online' | 'card_present';
  method?: string;
  cardCategory?: 'domestic' | 'premium' | 'international';
  applicationFeeBps?: number | null;
  applicationFeeFixed?: number | null;
}

export interface FeeComponent {
  key: string;
  label: string;
  amount: number;
  detail?: string;
}

export interface FeeEstimate {
  currency: string;
  gross: number;
  processorFee: number;
  applicationFee: number;
  merchantNet: number;
  platformGross: number;
  components: FeeComponent[];
  ruleId: string | null;
  catalogVersionId: string | null;
  priced: boolean;
  isEstimate: true;
  note?: string;
}

export interface FeeCompareInput {
  amount: number;
  currency: string;
  country?: string | null;
  channel?: 'online' | 'card_present';
  methods: string[];
  applicationFeeBps?: number | null;
  applicationFeeFixed?: number | null;
}

export interface FeeRecommendation {
  currency: string;
  recommended: string | null;
  recommendedFee: number | null;
  cardFee: number | null;
  savingsVsCard: number | null;
  ranked: Array<{ method: string; totalFee: number; merchantNet: number; priced: boolean }>;
  reason: string;
}

export interface FeeBreakEvenInput {
  currency: string;
  country?: string | null;
  channel?: 'online' | 'card_present';
  method?: string;
  cardCategory?: 'domestic' | 'premium' | 'international';
  applicationFeeBps?: number | null;
  applicationFeeFixed?: number | null;
  targetNetFraction?: number;
}

export interface FeeBreakEven {
  currency: string;
  breakEvenAmount: number | null;
  targetNetFraction: number;
  note?: string;
}

/** A public pricing plan and its currently effective version. */
export interface Plan {
  id: string;
  key: string;
  name: string;
  description: string;
  is_public: boolean;
  status: string;
  active_version: {
    id: string;
    monthly_fee: number;
    currency: string;
    fee_percent_bps: number;
    fee_fixed: number;
    included_volume: number | null;
    overage_percent_bps: number | null;
    zero_platform_fee: boolean;
  } | null;
}

export interface PlanAssignment {
  assignment_id: string;
  plan: { id: string; key: string; name: string };
  version: {
    id: string;
    monthly_fee: number;
    fee_percent_bps: number;
    fee_fixed: number;
    zero_platform_fee: boolean;
  };
  started_at: string;
}
